import { Button, Modal, Tooltip, Upload, Input, Table, Space, Popconfirm, Empty, Spin } from 'antd'
import { useSelector } from 'react-redux'
import { EditOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { getLoginToken, requestMyActivities, store } from './store/store'
import { getRemoteUrl, handleRemoteAuthFailure } from './store/request'
import { useState, useRef, useEffect, useCallback } from 'react';
import { reqChangeActivityName, reqDeleteActivity, setActivities, setActivityName, setSelectedActivity, asyncReqActivityTrace } from './store/activities'
import { useConversionStatus } from './useConversionStatus'
import { MIN_TABLE_WIDTH, ROW_LAYOUT_MIN_WIDTH, clampSize, defaultSplitSize, loadSplit, saveSplit, formatActivityDate } from './activitiesLayout'
import Splitter from './Splitter'
import './css/activities.css'

import ActivityCharts from './ActivityCharts'

import Map from "./map/Map"
import { Layers, TileLayer, VectorLayer } from "./map/layers"
import { Controls, FullScreenControl, ZoomControl } from "./map/controls"
import { OsmCache } from "./map/sources/osm"
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import LineString from 'ol/geom/LineString'
import {Style,Stroke} from 'ol/style'
import * as olExtent from 'ol/extent'


function getActivityName(name) {
  if (name && name.length > 0)
    return name
  else
    return "Без имени"
}

function formatDuration(seconds) {
  if (seconds == null) return ''
  var date = new Date(null)
  date.setSeconds(seconds)
  return date.toISOString().substring(11, 19)
}

// Active table-pane size in px for the current axis. Falls back to the shared
// per-axis default and re-clamps whenever the panes container resizes.
function activeSplitValue(split, axis, panesSize) {
  const v = split[axis]
  if (v == null || !isFinite(v)) return defaultSplitSize(axis, panesSize)
  return clampSize(v, panesSize)
}

function ActivitiesTable({ paneStyle }) {
  const {activities, activitiesLoading, selectedActivity} = useSelector(state => state.activities)

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});

  useEffect(() => {
    if (activities === undefined && activitiesLoading === false) {
      requestMyActivities()
    }
  }, [activities, activitiesLoading])

  // Use it to set focus on modal form apperance
  const textInput = useRef(null)

  const showChangeNameForm = (record) => {
    const data = {
      name: record.name,
      id: record.id
    }

    setModalData(data)
    setIsModalVisible(true)

    textInput.current.focus()
  }

  const handleRowClick = (record) => {
    store.dispatch(setSelectedActivity(record))
    store.dispatch(asyncReqActivityTrace(record.id))
  }

  const handleDeleteActivity = (activityId) => {
    reqDeleteActivity(activityId)

    const newActivities = activities.filter((item) => item.id !== activityId);
    store.dispatch(setActivities({ activities: newActivities }))
  }

  const handleModalFormOk = () => {
    reqChangeActivityName(modalData.id, modalData.name)

    const payload = {...modalData}

    store.dispatch(setActivityName(payload))

    setIsModalVisible(false);
  }

  const handleModalFormCancel = () => {
    setIsModalVisible(false);
  }

  const handleModalInputChange = (e) => {
    const newData = {...modalData, name:e.target.value}
    setModalData(newData)
  }

  const handleKeyDown = (e) => {
    const list = activities || []
    if (!list.length || !selectedActivity) return
    const idx = list.findIndex((a) => a.id === selectedActivity.id)
    let next
    if (e.key === 'ArrowDown') next = idx + 1
    else if (e.key === 'ArrowUp') next = idx - 1
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = list.length - 1
    else return
    if (next < 0 || next >= list.length) return
    e.preventDefault()
    const record = list[next]
    store.dispatch(setSelectedActivity(record))
    store.dispatch(asyncReqActivityTrace(record.id))
  }

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      ellipsis: true,
      render: (_,record) => {
        var name = getActivityName(record.name)
        return (<div title={name}>{name}</div>)
      }
    },
    {
      title: 'Дата',
      dataIndex: 'start_time',
      width: 130,
      render: (_,record) => {
        return (<div>{formatActivityDate(record.start_time)}</div>)
      }
    },
    {
      title: 'Км',
      dataIndex: 'distance',
      align: 'right',
      width: 60,
      render: (_, record) => {
        return (<div>{record.distance ? record.distance.toFixed(2) : ""}</div>)
      }
    },
    {
      title: 'Вт',
      dataIndex: 'np',
      align: 'right',
      width: 60,
      render: (_,record) => {
        return (<div>{record.np ? Math.round(record.np): ""}</div>)
      }
    },
    {
      title: 'Действия',
      key: 'action',
      width: 70,
      render: (_, record) => {
        return (<span className="row-actions">
          <Popconfirm title="Точно удалить?" okText="Да" cancelText="Нет" onConfirm={() => handleDeleteActivity(record.id)}>
              <Button size="small" type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
          <Tooltip title="Изменить название">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => showChangeNameForm(record)} />
          </Tooltip>
        </span>)
      }
    }
  ]

  return (<section className="activities-pane activities-table-pane" style={paneStyle}>
    <div className="pane-title">Список активностей · <b>{activities ? activities.length : 0}</b> · ↑/↓ — выбор</div>
    <div className="table-scroll" tabIndex={0} onKeyDown={handleKeyDown}>
      <Table
        size='small'
        loading={activitiesLoading}
        columns={columns}
        dataSource={activities}
        rowKey='id'
        pagination={false}
        tableLayout='fixed'
        scroll={{ x: MIN_TABLE_WIDTH }}
        rowClassName={(record) => selectedActivity && selectedActivity.id === record.id ? 'is-selected' : ''}
        onRow = {
          (record) => {
            return {
              onClick:() => {handleRowClick(record)}
            }
          }
        }
      />
    </div>

    <Modal title="Изменить название" visible={isModalVisible} onOk={handleModalFormOk} onCancel={handleModalFormCancel}>
      <Input ref={textInput} placeholder="Введите новое название" onChange={handleModalInputChange} value={modalData.name} />
    </Modal>
  </section>
    )
}

function ActivityDetails() {
  const {selectedActivity} = useSelector(state => state.activities)

  if (!selectedActivity) {
    return (<div className="details-scroll"><Empty description='Активность не выбрана'/></div>)
  }

  var name = getActivityName(selectedActivity.name)
  const chips = [
    { label: 'Общее время', value: formatDuration(selectedActivity.elapsed_duration) },
    { label: 'Время в движении', value: formatDuration(selectedActivity.timer_duration) },
    { label: 'Дистанция', value: selectedActivity.distance != null ? selectedActivity.distance.toFixed(2) + " км" : null },
    { label: 'Средний пульс', value: selectedActivity.avg_hr != null ? String(Math.round(selectedActivity.avg_hr)) : null },
    { label: 'Расчетная мощность', value: selectedActivity.np != null ? String(Math.round(selectedActivity.np)) : null },
    { label: 'Интенсивность', value: selectedActivity.if != null ? selectedActivity.if.toFixed(2) : null },
    { label: 'Нагрузка', value: selectedActivity.tss != null ? String(Math.round(selectedActivity.tss)) : null },
    { label: 'Средняя мощность', value: selectedActivity.avg_pwr != null ? String(Math.round(selectedActivity.avg_pwr)) : null },
    { label: 'KJ (CAL)', value: selectedActivity.kj != null ? `KJ (CAL): ${Math.round(selectedActivity.kj)}` : null },
    { label: 'Средняя скорость', value: '###' },
    { label: 'Спуск/подъём', value: '###/###' },
  ]

  return (
    <div className="details-scroll">
      <div>
        <div className="det-name">{name}{selectedActivity.outdoor === 0 && (<span className="badge">в помещении</span>)}</div>
        <div className="det-date">{formatActivityDate(selectedActivity.start_time)}</div>
      </div>
      <div className="chips">
        {chips.filter((c) => c.value != null).map((c) => (
          <span className="chip" key={c.label}>
            {c.label && <i>{c.label}</i>}
            <b>{c.value}</b>
          </span>
        ))}
      </div>
      <div className="details-grid">
        <div className="map-side"><ActivityMap /></div>
        <div className="charts-side"><ActivityCharts key={selectedActivity.id} /></div>
      </div>
    </div>
    )
}

function ActivityMap() {
  const {selectedActivityTrace, selectedActivity} = useSelector(state => state.activities)
  const [zoom] = useState(10);

  if (selectedActivity && selectedActivity.outdoor === 0) {
    return (<div className="map-empty">Тренировка в помещении — карта не требуется</div>)
  }

  if (!selectedActivityTrace || selectedActivityTrace.length === 0){
    return (<Spin tip="Грузим трек..."> <Map /> </Spin>)
  }

  let coords = []
  for (const p of selectedActivityTrace) {
    if (p['latitude'] && p['longitude'])
      coords.push([p['longitude'], p['latitude']])
  }

  var feature = new Feature({
    geometry: new LineString(coords)
  })

  const pathStyle = new Style({
    stroke: new Stroke({
      color: "blue",
      width: 2
    })
  })

  feature.getGeometry().transform('EPSG:4326', 'EPSG:3857')
  var vectorSource = new VectorSource({features: [feature]})

  const extent = feature.getGeometry().getExtent()
  const center = olExtent.getCenter(extent)

  return (
    <Map center={center} zoom={zoom}>
      <Layers>
        <TileLayer
          source={new OsmCache()}
          zIndex={0}
        />
        <VectorLayer
          source={vectorSource}
          style={pathStyle}
          zIndex={1}
        />
      </Layers>
      <Controls>
        <FullScreenControl />
        <ZoomControl />
      </Controls>
    </Map>
  )
}

// Adaptive master–details switching: observes the panes container and reports
// the layout mode (row ≥ ROW_LAYOUT_MIN_WIDTH, else column fallback) plus the
// live container size along both axes.
function usePanesMode() {
  const panesRef = useRef(null)
  const [isRow, setIsRow] = useState(true)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = panesRef.current
    if (!el) return undefined
    const update = () => {
      const width = el.clientWidth
      setSize({ width, height: el.clientHeight })
      // Fallback to row mode while the container is not laid out (jsdom,
      // hidden tab, initial paint) — a real width flips the mode correctly.
      setIsRow(width <= 0 || width >= ROW_LAYOUT_MIN_WIDTH)
    }
    update()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return { panesRef, isRow, size }
}

function ViewActivities() {
  // Shared per-file conversion status (processing count, errors).
  const { files, errors, register } = useConversionStatus()

  // Single-file upload: antd keeps the list to show a per-file progress bar,
  // then clears it once the file reaches the server.
  const [singleFileList, setSingleFileList] = useState([])
  const [batchUploading, setBatchUploading] = useState(false)
  const [batchFileList, setBatchFileList] = useState([])
  const [uploadErrors, setUploadErrors] = useState([])

  // Synchronous guard for the batch upload. antd calls `onChange` once per
  // selected file (when `beforeUpload` returns false) with the SAME full
  // fileList; React batches the `batchUploading` state update, so that state
  // guard would let a second synchronous call slip through and re-POST the
  // same files (creating duplicate conversion rows). A ref is updated
  // synchronously and blocks the duplicate submission reliably.
  const batchInFlightRef = useRef(false)

  // Master–details split state. Each axis keeps its own stored px; the active
  // axis value is re-derived (defaulted + clamped) whenever the mode flips.
  const { panesRef, isRow, size } = usePanesMode()
  const axis = isRow ? 'x' : 'y'
  const panesSize = axis === 'x' ? size.width : size.height
  const [split, setSplit] = useState(() => loadSplit() || { x: null, y: null })
  const tablePx = activeSplitValue(split, axis, panesSize)

  const handleSizeChange = useCallback((px, persist) => {
    const clamped = clampSize(px, panesSize)
    setSplit((prev) => {
      const next = { ...prev, [axis]: clamped }
      if (persist) saveSplit(next)
      return next
    })
  }, [axis, panesSize])

  const handleReset = useCallback(() => {
    setSplit((prev) => {
      const next = { ...prev, [axis]: defaultSplitSize(axis, panesSize) }
      saveSplit(next)
      return next
    })
  }, [axis, panesSize])

  const singleUploadProps = {
    name: 'files',
    multiple: false,
    accept: '.fit',
    action: getRemoteUrl('activities/uploads'),
    headers: {
      'Authorization': `Bearer ${getLoginToken()}`
    },
    fileList: singleFileList,
    onChange(info) {
      let newFileList = [...info.fileList]
      let allDone = true

      newFileList = newFileList.map((f) => {
        if (f.status === 'done' && f.response && f.response.files) {
          register(f.response.files)
        } else if (f.status === 'error') {
          if (f.response && f.response.code === 'Unauthorized') {
            handleRemoteAuthFailure(getRemoteUrl('activities/uploads'), {
              headers: { 'Authorization': `Bearer ${getLoginToken()}` },
            })
          }
          const message =
            (f.response && f.response.detail) ||
            (f.error && f.error.message) ||
            'Не удалось загрузить файл'
          setUploadErrors(prev => [...prev, { filename: f.name, message }])
        }
        if (!f.status || f.status === 'uploading') allDone = false
        return f
      })

      if (allDone) newFileList = []
      setSingleFileList(newFileList)
    },
  }

  // Batch upload: one multipart POST for all selected files; no per-file
  // listing or progress bars — only a subtle in-flight state on the button.
  const batchUploadProps = {
    multiple: true,
    accept: '.fit',
    showUploadList: false,
    beforeUpload: () => false,
    fileList: batchFileList,
    onChange(info) {
      const selected = info.fileList
      if (!selected.length) return
      if (batchInFlightRef.current) return
      batchInFlightRef.current = true

      setBatchUploading(true)
      setBatchFileList([])

      const formData = new FormData()
      selected.forEach((f) => formData.append('files', f.originFileObj, f.name))

      fetch(getRemoteUrl('activities/uploads'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getLoginToken()}` },
        body: formData,
      })
        .then(async (response) => {
          if (!response.ok) {
            if (response.status === 401) {
              handleRemoteAuthFailure(getRemoteUrl('activities/uploads'), {
                headers: { 'Authorization': `Bearer ${getLoginToken()}` },
              })
            }
            let detail = `Статус ответа сервера ${response.status}`
            try {
              const body = await response.json()
              if (body && body.detail) detail = body.detail
            } catch (e) { /* ignore */ }
            throw new Error(detail)
          }
          return response.json()
        })
        .then((data) => {
          if (data && data.files) register(data.files)
        })
        .catch((e) => {
          setUploadErrors(prev => [
            ...prev,
            { filename: selected.map((f) => f.name).join(', '), message: e.message },
          ])
        })
        .finally(() => {
          batchInFlightRef.current = false
          setBatchUploading(false)
        })
    },
  }

  const pendingCount = files.length
  const MAX_VISIBLE = 20
  const visibleFiles = files.slice(0, MAX_VISIBLE)
  const hiddenCount = pendingCount - visibleFiles.length

  return (
    <div className={`activities-view${isRow ? ' is-row-mode' : ''}`}>
      <div className="activities-toolbar">
        {pendingCount > 0 && (
          <div>
            <Space size='small'>
              <Spin size='small' />
              <span>Обработка файлов: {pendingCount}</span>
            </Space>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {visibleFiles.map((file) => (
                <li key={file.id}>{file.filename} — обработка...</li>
              ))}
              {hiddenCount > 0 && <li>+{hiddenCount} более</li>}
            </ul>
          </div>
        )}

        {[...uploadErrors, ...errors].length > 0 && (
          <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
            {[...uploadErrors, ...errors].map((err, idx) => (
              <li key={idx} style={{ color: 'red' }}>
                {err.filename} - {err.message}
              </li>
            ))}
          </ul>
        )}

        <Space size='small' align='center'>
          <Upload {...singleUploadProps}>
            <Button icon={<UploadOutlined />}>Загрузить .fit файл</Button>
          </Upload>
          <Upload {...batchUploadProps}>
            <Button
              icon={<UploadOutlined />}
              disabled={batchUploading}
              loading={batchUploading}
            >
              {batchUploading ? 'Загрузка…' : 'Пакетная загрузка'}
            </Button>
          </Upload>
        </Space>
      </div>

      <div className="activities-panes" ref={panesRef} data-mode={isRow ? 'row' : 'column'}>
        <ActivitiesTable paneStyle={{ flex: `0 0 ${tablePx}px` }} />
        <Splitter
          axis={axis}
          tableSize={tablePx}
          panesSize={panesSize}
          onSizeChange={handleSizeChange}
          onReset={handleReset}
          labelPrefix="Разделитель"
        />
        <section className="activities-pane activities-details-pane">
          <div className="pane-title">Подробности</div>
          <ActivityDetails/>
        </section>
      </div>
    </div>
  )
}

export default ViewActivities;